import React, { useContext, useState, useRef } from 'react';
import Title from '../../library/Title';
import { StateContext } from "../../../state/index";
import { Button, ButtonContainer } from "../../library/Buttons";
import StartOver from '../../library/StartOver';
import Form from 'react-jsonschema-form';
import { substituteVariablesInString } from '../../../utils/substitutions';
import { get, pick } from 'lodash/object';
import './form.scss';

export const JsonFormView = props => {
  const [{configuration: {recipientLogin: {form: formConfig}}, payment, paymentNetwork }] = useContext(StateContext);

  const dataForVariableSubstitutions = {
    payment,
    paymentNetwork
  };

  const formConfigWithVariableSubstitutions = JSON.parse(substituteVariablesInString(JSON.stringify(formConfig), dataForVariableSubstitutions));

    return <JsonForm formConfig={formConfigWithVariableSubstitutions} {...props}/>
};

export const JsonForm = ({onSubmit, isFetching, formConfig}) => {
  let formRef = useRef(null);
  const { jsonFormProps } = formConfig;
  const [formData, setFormData] = useState(jsonFormProps.formData);
  const [canSubmit, setCanSubmit] = useState(false);

  const transformErrors = errors => {
    const errorsByField = {};
    const visibleErrors = [];
    const { validationErrors } = formConfig;

    errors.forEach(error => {
      const {property} = error;
      const fieldName = property.substring(1); // remove leading period that react-jsonschema includes on property names
      const errorMessage = get(validationErrors, [fieldName, error.name]) ? validationErrors[fieldName][error.name] : error.message;

      // by default, react-jsonschema-form will show a message for every error type, even if we want to have the same message for multiple error types.
      // for instance, in the case of a JetBlue True Blue account number which is exactly 10 numeric digits, '123', '1234567890123', and 'abcdefghij' could
      // all use the same error message 'True Blue account numbers should be 10 numeric digits'.
      // instead, we'll keep track of our transformed error messages and make sure only unique messages are displayed per field.
      // this way the user won't get bombarded with duplicate error messages
      const fieldErrorMessage = `${property}${errorMessage}`;
      if(!errorsByField[fieldErrorMessage]) {
        errorsByField[fieldErrorMessage] = true;
        visibleErrors.push({
          ...error,
          message: errorMessage,
        });
      }
    });

    return visibleErrors;
  };

  const filterFormData = (formData) => {
    // when showing and then hiding react-jsonschema-form fields, the removed fields aren't removed from the form data.
    // as a workaround, we're filtering what fields get submitted based on what the current values are, which is configurable per the form config
    const { filterFieldsByValue } = formConfig;

    /*
        // sample config is an array of objects with the current field values and the corresponding fields to submit
        // configs should be ordered from most-to-least specific, as the first one that matches will be used to apply the filters
        [
            {
                currentValues: {
                    createAccount: true,
                },
                fieldsToSubmit: ["createAccount, emailAddress, accountNumber"]
            }
        ]
    */

    if(!filterFieldsByValue) return formData;

    let filteredFormData = formData;
    filterFieldsByValue.forEach(filterConfig => {
      let match = true;
      const { expectedValues, fieldsToInclude } = filterConfig;

      // eslint-disable-next-line no-unused-vars
      for(let fieldName in expectedValues){
        if(expectedValues[fieldName] !== formData[fieldName]) match = false
      }

      if(match) {
        filteredFormData = pick(formData, fieldsToInclude);
      }
    });

    return filteredFormData;
  };

  const onSubmitForm = ({formData}) => {
    const filteredFormData = filterFormData(formData);
    onSubmit(filteredFormData);
  };

  return (<>
    <Title>
      <h1>{formConfig.title}</h1>
    </Title>
    <section className="main">
      <p style={{textAlign: 'center'}}>{formConfig.description}</p>
    </section>
    <section className="middle">
      <div className="form-container">
        <Form
          schema={jsonFormProps.schema}
          uiSchema={jsonFormProps.uiSchema}
          formData={formData}
          onChange={({formData, errors}) => {
            setFormData(formData);
            setCanSubmit(errors.length === 0);
          }}
          onSubmit={onSubmitForm}
          transformErrors={transformErrors}
          liveValidate={true}
          showErrorList={false}
          ref={(form) => {formRef = form;}}
        ><input type="submit" style={{display: 'none'}}/></Form>
      </div>
      <ButtonContainer>
        <Button
          name="button"
          id="button"
          disabled={!canSubmit}
          loading={isFetching}
          onClick={() => formRef.submit()}
          small
        >
          Submit
        </Button>
      </ButtonContainer>
    </section>
    <section>
      <StartOver/>
    </section>
  </>);
};

export default JsonFormView;
